import { useCallback } from 'react';
import classNames from 'classnames';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/large/WarningTriangle';

type AddressInputProps = {
  token?: ChainflipToken;
  address: string | undefined;
  setAddress: (address: string) => void;
  focusOnMount?: boolean;
  className?: string;
  inputLabelClassName?: string;
  error?: string;
  placeholder: string;
  fullWidth?: true;
  allowFlexibleInput?: boolean;
  warning?: string;
};

// lean copy of AddressInput from swap flow
export const AddressInput = ({
  token,
  address,
  setAddress,
  focusOnMount = false,
  className,
  inputLabelClassName,
  error,
  placeholder,
  fullWidth,
  allowFlexibleInput = false,
  warning,
}: AddressInputProps): React.JSX.Element => {
  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newValue = e.target.value;

      if (!allowFlexibleInput && !/^[0-9a-z]*$/i.test(newValue)) return;

      setAddress(newValue);
    },
    [allowFlexibleInput, setAddress],
  );
  return (
    <div className={fullWidth ? 'w-full' : ''}>
      <div
        className={classNames(
          'group relative rounded-md border border-cf-gray-4 bg-cf-gray-3-5 shadow-grayPop3 transition',
          'focus-within:border-cf-gray-5 focus-within:bg-cf-gray-4',
          'hover:border-cf-gray-5 hover:bg-cf-gray-4',
          'active:border-cf-gray-4 active:bg-cf-gray-3-5',
          error && 'border-cf-red-1',
          className,
        )}
      >
        <input
          className="peer z-0 w-full bg-transparent p-3 text-14 text-cf-white outline-none"
          placeholder=""
          onChange={onChange}
          spellCheck={false}
          value={address}
          autoFocus={focusOnMount}
        />
        <div
          className={classNames(
            'pointer-events-none absolute left-0 top-0 flex items-center p-3 text-14 text-cf-light-1 transition',
            'group-focus-within:translate-x-[-5%] group-focus-within:translate-y-[calc(-50%-1px)] group-focus-within:scale-[90%]',
            'peer-[:not(:placeholder-shown)]:translate-x-[-5%] peer-[:not(:placeholder-shown)]:translate-y-[calc(-50%-1px)] peer-[:not(:placeholder-shown)]:scale-[90%]',
          )}
        >
          <div className="flex flex-row items-center gap-1">
            {token && <ChainLogo size="medium" variant="monochromatic" chain={token.chain} />}
            <div>{placeholder}</div>
          </div>
          <div
            className={classNames(
              // overlay red border when showing error
              'absolute inset-x-2 bottom-3 top-[49%] -z-10 bg-cf-gray-3-5',
              'transition group-focus-within:bg-cf-gray-4 group-hover:bg-cf-gray-4 group-active:bg-cf-gray-3-5',
              inputLabelClassName,
            )}
          />
        </div>
        {token?.chainflipId === 'Trx' && !error && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cf-orange-1">
            <WarningTriangleIcon />
          </div>
        )}
      </div>
      {error ? (
        <span className="text-12 text-cf-red-1">{error}</span>
      ) : (
        (token?.chainflipId === 'Trx' || warning) && (
          <div className="mt-1 px-2 font-aeonikMedium text-12 leading-tight text-cf-orange-1">
            {warning ??
              'Smart contract wallets (e.g. Safe) are not supported for TRX. Funds may be lost'}
          </div>
        )
      )}
    </div>
  );
};
export default AddressInput;
